"use client";
import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { ErrorBoundary } from "react-error-boundary";
import { useReportClientError } from "./reportClientError";
import NotFound from "../../components/common/NotFound";
import { getLastUserEvent } from "./global-events";
import { useAppSelector } from "../../components/slice/hooks";
import { getActiveUser } from "../../components/slice/auth-slice";

const SCREENSHOT_CACHE_MAX_AGE_MS = 2 * 60 * 1000;
const SCREENSHOT_REFRESH_INTERVAL_MS = 15 * 1000;
const SCREENSHOT_TIMEOUT_MS = 1500;

let cachedScreenshot = null;
let lastScreenshotAttemptAt = 0;
let activeScreenshotCaptures = 0;

function ErrorFallback({ chunkError }) {
  if (chunkError) {
    return (
      <div style={styles.wrapper}>
        <p>We’ve updated the app. Refreshing now...</p>
      </div>
    );
  }
  return (
    <div style={styles.wrapper}>
      <NotFound />
      <button
        style={styles.button}
        onClick={() => window.location.reload(true)}
      >
        Reload Page
      </button>
      <p style={styles.info}>If the issue continues, please contact support.</p>
    </div>
  );
}

function formatLastUserEvent(event) {
  if (!event) return "N/A";

  try {
    const { type, target } = event;
    if (!target || typeof target.tagName !== "string")
      return `${type} on [unknown element]`;

    const tag = target.tagName.toLowerCase();
    const id = target.id ? `#${target.id}` : "";
    const classes = target.className
      ? `.${target.className.toString().trim().split(/\s+/).join(".")}`
      : "";

    return `${type} on ${tag}${id}${classes}`;
  } catch {
    return "Error formatting event";
  }
}

async function withTimeout(promise, ms = 2000) {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Screenshot timeout")), ms)
    ),
  ]);
}

function isScreenshotCaptureError(error) {
  const message = String(error?.message || error || "");
  return (
    activeScreenshotCaptures > 0 &&
    (message.includes("removeChild") || message.includes("insertBefore"))
  );
}

function getCachedScreenshotMeta() {
  if (!cachedScreenshot) return {};

  const ageMs = Date.now() - cachedScreenshot.capturedAt;
  if (ageMs > SCREENSHOT_CACHE_MAX_AGE_MS) return {};

  return {
    screenshotBase64: cachedScreenshot.base64,
    screenshotCapturedAt: new Date(cachedScreenshot.capturedAt).toISOString(),
    screenshotAgeMs: ageMs,
    screenshotPathname: cachedScreenshot.pathname,
  };
}

async function captureScreenshot() {
  try {
    if (typeof window !== "undefined" && document?.body) {
      activeScreenshotCaptures += 1;
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await withTimeout(
        html2canvas(document.body, {
          scale: 0.35,
          useCORS: true,
          logging: false,
          backgroundColor: window.getComputedStyle(document.body).backgroundColor,
        }),
        SCREENSHOT_TIMEOUT_MS
      );
      return canvas.toDataURL("image/png");
    }
  } catch (err) {
    if (err?.message !== "Screenshot timeout") {
      console.warn("Screenshot capture failed:", err);
    }
  } finally {
    setTimeout(() => {
      activeScreenshotCaptures = Math.max(0, activeScreenshotCaptures - 1);
    }, SCREENSHOT_TIMEOUT_MS);
  }
  return null;
}

async function refreshScreenshotCache(force = false) {
  const now = Date.now();
  if (!force && now - lastScreenshotAttemptAt < SCREENSHOT_REFRESH_INTERVAL_MS) {
    return;
  }

  lastScreenshotAttemptAt = now;
  const base64 = await captureScreenshot();

  if (base64) {
    cachedScreenshot = {
      base64,
      capturedAt: Date.now(),
      pathname: window.location.pathname,
    };
  }
}

export default function ErrorBoundaryWrapper({ children }) {
  const pathname = usePathname();
  const reportClientError = useReportClientError();
  const [hasError, setHasError] = useState(false);
  const [chunkError, setChunkError] = useState(false);
  const { tokenData } = useAppSelector(getActiveUser);

  const getCleanElement = (el) => {
    if (!el) return null;
    if (["BODY", "HTML"].includes(el.tagName)) return null;
    return el.outerHTML?.slice(0, 300);
  };

  // Global JS error + rejection handling
  useEffect(() => {
    const handleError = async (event) => {
      try {
        const error = event?.error || event?.message || "Unknown client error";

        if (isScreenshotCaptureError(error)) {
          event?.preventDefault?.();
          return;
        }

        const isChunkError =
          error?.name === "ChunkLoadError" ||
          error?.message?.includes("Loading chunk");

        setChunkError(isChunkError);

        const metaData = {
          lastUserEvent: formatLastUserEvent(getLastUserEvent()),
          activeElement: getCleanElement(document.activeElement),
          hoveredElement: getCleanElement(document.querySelector(":hover")),
          ...getCachedScreenshotMeta(),
        };

        const details = {
          type: isChunkError ? "ChunkLoadError" : "ClientError",
          message: error?.toString(),
          pathname: window.location.pathname,
        };

        reportClientError(event, details, metaData, tokenData?.token);

        if (isChunkError) {
          setTimeout(() => window.location.reload(true), 1500);
        }
      } catch (_) {}
      setHasError(true);
    };

    const handleRejection = async (event) => {
      try {
        const reason = event?.reason || "Unhandled promise rejection";

        if (isScreenshotCaptureError(reason)) {
          event?.preventDefault?.();
          return;
        }

        const metaData = {
          lastUserEvent: formatLastUserEvent(getLastUserEvent()),
          activeElement: getCleanElement(document.activeElement),
          hoveredElement: getCleanElement(document.querySelector(":hover")),
          ...getCachedScreenshotMeta(),
        };

        const details = {
          type: "UnhandledPromiseRejection",
          message: reason?.toString(),
          pathname: window.location.pathname,
        };

        reportClientError(event, details, metaData, tokenData?.token);
      } catch (_) {}
      setHasError(true);
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleRejection);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleRejection);
    };
  }, [reportClientError, tokenData]);

  useEffect(() => {
    const scheduleRefresh = (force = false) => {
      setTimeout(() => refreshScreenshotCache(force), 300);
    };

    scheduleRefresh(true);

    const handleUserActivity = () => scheduleRefresh(false);
    const handleVisibilityChange = () => {
      if (!document.hidden) scheduleRefresh(true);
    };

    window.addEventListener("click", handleUserActivity, true);
    window.addEventListener("keydown", handleUserActivity, true);
    window.addEventListener("scroll", handleUserActivity, true);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("click", handleUserActivity, true);
      window.removeEventListener("keydown", handleUserActivity, true);
      window.removeEventListener("scroll", handleUserActivity, true);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    setHasError(false);
    setChunkError(false);
  }, [pathname]);

  // React render errors
  const handleReactError = async (error, info) => {
    try {
      if (isScreenshotCaptureError(error)) return;

      const isChunkError =
        error?.name === "ChunkLoadError" ||
        error?.message?.includes("Loading chunk");

      setChunkError(isChunkError);

      const metaData = {
        lastUserEvent: formatLastUserEvent(getLastUserEvent()),
        activeElement: getCleanElement(document.activeElement),
        hoveredElement: getCleanElement(document.querySelector(":hover")),
        componentStack: info?.componentStack,
        ...getCachedScreenshotMeta(),
      };

      const details = {
        type: isChunkError ? "ChunkLoadError" : "ReactRenderError",
        message: error?.message || error?.toString(),
        pathname: window.location.pathname,
      };

      reportClientError(error, details, metaData, tokenData?.token);

      if (isChunkError) {
       setTimeout(() => location.replace(location.href), 1500);
      }
    } catch (_) {}
    setHasError(true);
  };

  if (hasError) {
    return <ErrorFallback chunkError={chunkError} />;
  }

  return (
    <ErrorBoundary
      key={pathname}
      resetKeys={[pathname]}
      onError={handleReactError}
      fallbackRender={({ resetErrorBoundary }) => (
        <ErrorFallback onRetry={resetErrorBoundary} />
      )}
    >
      {children}
    </ErrorBoundary>
  );
}

const styles = {
  wrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "3rem",
    minHeight: "100vh",
    backgroundColor: "#fff0f0",
    color: "#800000",
    textAlign: "center",
  },
  button: {
    marginTop: "2rem",
    padding: "0.75rem 1.5rem",
    fontSize: "1rem",
    backgroundColor: "#800000",
    color: "#ffffff",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
  },
  info: {
    marginTop: "1rem",
    fontSize: "0.9rem",
    color: "#444",
  },
};
